import React, { useState } from 'react';
import { useScheduleInterview, useUpdateInterview, useCreateGoogleMeetLink, usePanelists } from '../../hooks/unified/useInterviewsQueries';
import type { InterviewPanelMember, Panelist } from '../../services/unified/types';

interface InterviewSchedulerModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  candidateName?: string;
  jobTitle?: string;
  onInterviewScheduled?: (interviewId: string) => void;
  existingInterview?: {
    id: string;
    scheduled_date: string;
    duration_minutes: number;
    interview_type: string;
    panel_members: string;
    notes?: string;
    google_meet_link?: string;
  } | null;
}

export const InterviewSchedulerModal: React.FC<InterviewSchedulerModalProps> = ({
  isOpen,
  onClose,
  applicationId,
  candidateName,
  jobTitle,
  onInterviewScheduled,
  existingInterview,
}) => {
  const [scheduledDate, setScheduledDate] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [interviewType, setInterviewType] = useState('Technical');
  const [panelMembers, setPanelMembers] = useState<InterviewPanelMember[]>([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('');
  const [notes, setNotes] = useState('');
  const [googleMeetLink, setGoogleMeetLink] = useState<string | null>(null);
  const [createGoogleMeet, setCreateGoogleMeet] = useState(true); // Auto-create Meet link by default
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Panelist suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredPanelists, setFilteredPanelists] = useState<Panelist[]>([]);

  const scheduleInterviewMutation = useScheduleInterview();
  const updateInterviewMutation = useUpdateInterview();
  const createGoogleMeetMutation = useCreateGoogleMeetLink();
  const { data: panelists } = usePanelists();

  // Load existing interview data when modal opens
  React.useEffect(() => {
    if (isOpen && existingInterview) {
      setIsEditMode(true);
      // Parse the scheduled date - convert from stored format to local datetime-local format
      const dateObj = new Date(existingInterview.scheduled_date);
      // Format as YYYY-MM-DDTHH:mm for datetime-local input
      const localDate = dateObj.toISOString().slice(0, 16);
      setScheduledDate(localDate);
      setDurationMinutes(existingInterview.duration_minutes);
      setInterviewType(existingInterview.interview_type);
      setNotes(existingInterview.notes || '');
      setGoogleMeetLink(existingInterview.google_meet_link || null);
      setCreateGoogleMeet(!existingInterview.google_meet_link); // Don't create new if already exists
      
      // Parse panel members from JSON string
      try {
        const members = JSON.parse(existingInterview.panel_members);
        setPanelMembers(members);
      } catch (e) {
        console.error('Failed to parse panel members:', e);
        setPanelMembers([]);
      }
    } else if (isOpen && !existingInterview) {
      setIsEditMode(false);
    }
  }, [isOpen, existingInterview]);

  const durationOptions = [30, 45, 60, 90, 120];
  const interviewTypes = ['Technical', 'HR', 'Managerial', 'Panel', 'Final Round'];

  const handleAddPanelMember = () => {
    if (!newMemberEmail) {
      setError('Email is required for panel members');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check for duplicate emails
    if (panelMembers.some(member => member.email === newMemberEmail)) {
      setError('This panel member has already been added');
      return;
    }

    const newMember: InterviewPanelMember = {
      email: newMemberEmail,
      name: newMemberName || undefined,
      role: newMemberRole || undefined,
    };

    setPanelMembers([...panelMembers, newMember]);
    setNewMemberEmail('');
    setNewMemberName('');
    setNewMemberRole('');
    setError(null);
  };

  const handleRemovePanelMember = (email: string) => {
    setPanelMembers(panelMembers.filter(member => member.email !== email));
  };

  // Handle email input change with filtering
  const handleEmailChange = (value: string) => {
    setNewMemberEmail(value);
    
    if (value.length > 0 && panelists) {
      const filtered = panelists.filter(p => 
        p.email.toLowerCase().includes(value.toLowerCase()) ||
        p.name?.toLowerCase().includes(value.toLowerCase()) ||
        p.role?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPanelists(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  // Select panelist from dropdown
  const handleSelectPanelist = (panelist: Panelist) => {
    setNewMemberEmail(panelist.email);
    setNewMemberName(panelist.name || '');
    setNewMemberRole(panelist.role || '');
    setShowSuggestions(false);
  };

  const handleCreateGoogleMeetLink = async () => {
    if (!scheduledDate) {
      setError('Please select a date and time first');
      return;
    }

    if (panelMembers.length === 0) {
      setError('Please add at least one panel member');
      return;
    }

    setError(null);

    try {
      const attendees = panelMembers.map(member => member.email);
      const summary = `Interview: ${jobTitle || 'Position'} - ${candidateName || 'Candidate'}`;

      const response = await createGoogleMeetMutation.mutateAsync({
        scheduled_date: scheduledDate,
        duration_minutes: durationMinutes,
        attendees,
        summary,
      });

      setGoogleMeetLink(response.meet_link);
      // Calendar event ID is stored in the response but not displayed in UI
    } catch (err: any) {
      setError(err.message || 'Failed to create Google Meet link');
    }
  };

  const handleCopyMeetLink = () => {
    if (googleMeetLink) {
      navigator.clipboard.writeText(googleMeetLink);
      // You could add a toast notification here
      alert('Google Meet link copied to clipboard!');
    }
  };

  const handleScheduleInterview = async () => {
    // Validation
    if (!scheduledDate) {
      setError('Please select a date and time');
      return;
    }

    if (panelMembers.length === 0) {
      setError('Please add at least one panel member');
      return;
    }

    if (!interviewType) {
      setError('Please select an interview type');
      return;
    }

    setError(null);

    try {
      // User inputs IST time, add seconds and IST timezone offset
      // Format: YYYY-MM-DDTHH:mm:ss+05:30
      const isoDateWithTimezone = `${scheduledDate}:00+05:30`;
      
      console.log('📅 Scheduling interview:', {
        userInput: scheduledDate,
        sentToBackend: isoDateWithTimezone,
        note: 'User inputs IST time, we add +05:30 timezone',
        isEditMode
      });

      if (isEditMode && existingInterview) {
        // Update existing interview
        const interview = await updateInterviewMutation.mutateAsync({
          interviewId: existingInterview.id,
          data: {
            scheduled_date: isoDateWithTimezone,
            duration_minutes: durationMinutes,
            interview_type: interviewType,
            panel_members: panelMembers,
            notes: notes || undefined,
          },
        });

        if (onInterviewScheduled) {
          onInterviewScheduled(interview.id);
        }
      } else {
        // Create new interview
        const interview = await scheduleInterviewMutation.mutateAsync({
          application_id: applicationId,
          scheduled_date: isoDateWithTimezone,
          duration_minutes: durationMinutes,
          interview_type: interviewType,
          panel_members: panelMembers,
          notes: notes || undefined,
          create_google_meet: createGoogleMeet, // Use checkbox state
        });

        if (onInterviewScheduled) {
          onInterviewScheduled(interview.id);
        }
      }

      // Reset form and close
      handleReset();
      onClose();
    } catch (err: any) {
      setError(err.message || (isEditMode ? 'Failed to update interview' : 'Failed to schedule interview'));
    }
  };

  const handleReset = () => {
    setScheduledDate('');
    setDurationMinutes(60);
    setInterviewType('Technical');
    setPanelMembers([]);
    setNewMemberEmail('');
    setNewMemberName('');
    setNewMemberRole('');
    setNotes('');
    setGoogleMeetLink(null);
    setCreateGoogleMeet(true);
    setError(null);
    setIsEditMode(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {isEditMode ? 'Edit Interview' : 'Schedule Interview'}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Interview Details Info */}
              {(candidateName || jobTitle) && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Interview Details</h4>
                  {candidateName && (
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Candidate:</span> {candidateName}
                    </p>
                  )}
                  {jobTitle && (
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Position:</span> {jobTitle}
                    </p>
                  )}
                </div>
              )}

              {/* Date and Time - Separate inputs for better UX */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={scheduledDate.split('T')[0] || ''}
                    onChange={(e) => {
                      const timePart = scheduledDate.split('T')[1] || '10:00';
                      setScheduledDate(`${e.target.value}T${timePart}`);
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    style={{ colorScheme: 'light' }}
                    disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    step="60"
                    value={scheduledDate.split('T')[1]?.substring(0, 5) || ''}
                    onChange={(e) => {
                      const datePart = scheduledDate.split('T')[0] || new Date().toISOString().split('T')[0];
                      setScheduledDate(`${datePart}T${e.target.value}`);
                    }}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    style={{ colorScheme: 'light' }}
                    disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Time in IST (Indian Standard Time)</p>
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration <span className="text-red-500">*</span>
                </label>
                <select
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                >
                  {durationOptions.map((duration) => (
                    <option key={duration} value={duration}>
                      {duration} minutes
                    </option>
                  ))}
                </select>
              </div>

              {/* Interview Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interview Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={interviewType}
                  onChange={(e) => setInterviewType(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                >
                  {interviewTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Create Google Meet Link Checkbox - only show for new interviews or if no existing link */}
              {(!isEditMode || !googleMeetLink) && (
                <div className="flex items-center">
                  <input
                    id="create-google-meet"
                    type="checkbox"
                    checked={createGoogleMeet}
                    onChange={(e) => setCreateGoogleMeet(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                  />
                  <label htmlFor="create-google-meet" className="ml-2 block text-sm text-gray-900">
                    Create Google Meet Link
                  </label>
                </div>
              )}

              {/* Show existing Google Meet link in edit mode */}
              {isEditMode && googleMeetLink && (
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-green-800">Google Meet Link</h3>
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="text"
                          value={googleMeetLink}
                          readOnly
                          className="flex-1 text-sm bg-white border-gray-300 rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(googleMeetLink);
                            alert('Google Meet link copied to clipboard!');
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy
                        </button>
                        <a
                          href={googleMeetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          Join Meet
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Panel Members */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Panel Members <span className="text-red-500">*</span>
                </label>
                
                {/* Add Panel Member Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="Email * (start typing for suggestions)"
                        value={newMemberEmail}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        onFocus={() => newMemberEmail && panelists && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                      />
                      
                      {/* Panelist Suggestions Dropdown */}
                      {showSuggestions && filteredPanelists.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredPanelists.map((panelist) => (
                            <div
                              key={panelist.id}
                              onClick={() => handleSelectPanelist(panelist)}
                              className="px-4 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-sm text-gray-900">
                                {panelist.name || panelist.email}
                              </div>
                              <div className="text-xs text-gray-600">{panelist.email}</div>
                              {panelist.role && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {panelist.role}
                                  {panelist.department && ` • ${panelist.department}`}
                                </div>
                              )}
                              <div className="text-xs text-gray-400 mt-0.5">
                                Used {panelist.usage_count} times
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Name (optional)"
                        value={newMemberName}
                        onChange={(e) => setNewMemberName(e.target.value)}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Role (optional)"
                        value={newMemberRole}
                        onChange={(e) => setNewMemberRole(e.target.value)}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPanelMember}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Panel Member
                  </button>
                </div>

                {/* Panel Members List */}
                {panelMembers.length > 0 && (
                  <div className="border border-gray-300 rounded-md divide-y divide-gray-200">
                    {panelMembers.map((member, index) => (
                      <div key={index} className="p-3 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{member.email}</p>
                          {(member.name || member.role) && (
                            <p className="text-xs text-gray-500">
                              {member.name && <span>{member.name}</span>}
                              {member.name && member.role && <span> • </span>}
                              {member.role && <span>{member.role}</span>}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemovePanelMember(member.email)}
                          className="ml-3 text-red-600 hover:text-red-800 focus:outline-none"
                          disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Google Meet Link Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Google Meet Link</h4>
                  {!googleMeetLink && (
                    <button
                      type="button"
                      onClick={handleCreateGoogleMeetLink}
                      disabled={createGoogleMeetMutation.isPending || !scheduledDate || panelMembers.length === 0}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createGoogleMeetMutation.isPending ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Creating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Create Google Meet Link
                        </>
                      )}
                    </button>
                  )}
                </div>

                {googleMeetLink && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-green-800">Google Meet Link Created</h3>
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="text"
                            value={googleMeetLink}
                            readOnly
                            className="flex-1 text-sm bg-white border-gray-300 rounded-md"
                          />
                          <button
                            type="button"
                            onClick={handleCopyMeetLink}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-green-700">
                          Calendar invitations will be sent to all panel members when you schedule the interview.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Add any additional notes or instructions for the interview..."
                  disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
            <button
              type="button"
              onClick={handleScheduleInterview}
              disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(scheduleInterviewMutation.isPending || updateInterviewMutation.isPending) ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {isEditMode ? 'Updating...' : 'Scheduling...'}
                </>
              ) : (
                isEditMode ? 'Update Interview' : 'Schedule Interview'
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              disabled={scheduleInterviewMutation.isPending || updateInterviewMutation.isPending}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
